import { useQueries, useQueryClient } from '@tanstack/react-query';

import { listContacts } from '../api/contacts';
import { FUNNEL_STAGES, type ContactDto, type Page } from '../api/types';

const KANBAN_LIMIT = 100;

export function useKanbanColumns() {
  const results = useQueries({
    queries: FUNNEL_STAGES.map((stage) => ({
      queryKey: ['contacts', 'kanban', stage],
      queryFn: () => listContacts({ stage, limit: KANBAN_LIMIT }),
      refetchOnWindowFocus: false,
    })),
  });

  const columns: Record<string, ContactDto[]> = {};
  let isLoading = false;
  let isError = false;
  FUNNEL_STAGES.forEach((stage, i) => {
    const r = results[i];
    columns[stage] = r?.data?.items ?? [];
    if (r?.isLoading) isLoading = true;
    if (r?.isError) isError = true;
  });

  return { columns, isLoading, isError };
}

/** Moves a contact card between the in-memory kanban query caches (rolled back on mutation error). */
export function useKanbanOptimisticMove() {
  const queryClient = useQueryClient();

  return {
    move(contact: ContactDto, fromStage: string, toStage: string): () => void {
      const fromKey = ['contacts', 'kanban', fromStage];
      const toKey = ['contacts', 'kanban', toStage];
      const prevFrom = queryClient.getQueryData<Page<ContactDto>>(fromKey);
      const prevTo = queryClient.getQueryData<Page<ContactDto>>(toKey);

      queryClient.setQueryData<Page<ContactDto>>(fromKey, (old) =>
        old ? { ...old, items: old.items.filter((c) => c.id !== contact.id) } : old,
      );
      queryClient.setQueryData<Page<ContactDto>>(toKey, (old) =>
        old
          ? { ...old, items: [{ ...contact, funnelStage: toStage }, ...old.items] }
          : { items: [{ ...contact, funnelStage: toStage }], nextCursor: null },
      );

      return () => {
        queryClient.setQueryData(fromKey, prevFrom);
        queryClient.setQueryData(toKey, prevTo);
      };
    },
    invalidateAll() {
      void queryClient.invalidateQueries({ queryKey: ['contacts', 'kanban'] });
      void queryClient.invalidateQueries({ queryKey: ['contacts', 'list'] });
    },
  };
}
